import { cn } from '@/utils';
import type { Wallet } from '@mysten/wallet-standard';

type WalletIconItem = Pick<Wallet, 'id' | 'name' | 'icon'>;

type WalletIconSize = 'xs' | 'sm' | 'md';

const SIZE_MAP: Record<WalletIconSize, string> = {
	xs: 'h-4.5 w-4.5 text-[8px]',
	sm: 'h-6 w-6 text-[10px]',
	md: 'h-7 w-7 text-xs',
};

interface WalletIconProps {
	item: WalletIconItem;
	size?: WalletIconSize;
	className?: string;
}

export function WalletIcon({ item, size = 'md', className }: WalletIconProps) {
	const effectiveSizeClass = SIZE_MAP[size];

	if (item.icon) {
		return (
			<img
				src={item.icon}
				alt={`${item.id || item.name} logo`}
				className={cn('inline-block rounded-full border border-border/60 bg-background object-cover shadow-sm', effectiveSizeClass, className)}
			/>
		);
	}

	return (
		<span
			className={cn(
				'inline-flex items-center justify-center rounded-full border border-border/60 bg-muted/90 font-semibold uppercase text-foreground shadow-sm',
				effectiveSizeClass,
				className,
			)}
		>
			{item.name?.slice(0, 2)}
		</span>
	);
}

interface WalletIconStackProps {
	items: WalletIconItem[];
	maxVisible?: number;
	size?: WalletIconSize;
	className?: string;
}

export function WalletIconStack({ items, maxVisible = 3, size = 'md', className }: WalletIconStackProps) {
	const visibleItems = items.slice(0, maxVisible);
	const remaining = Math.max(0, items.length - visibleItems.length);

	return (
		<span className={cn('flex items-center -space-x-2', className)}>
			{visibleItems.map((item) => (
				<WalletIcon key={item.id || item.name} item={item} size={size} />
			))}
			{remaining > 0 ? (
				<span
					className={cn(
						'inline-flex items-center justify-center rounded-full border border-dashed border-border/60 bg-background/80 font-semibold text-foreground/80 shadow-sm',
						SIZE_MAP[size],
					)}
				>
					+{remaining}
				</span>
			) : null}
		</span>
	);
}

export default WalletIconStack;
